// src/components/settings/AppVersionCard.js
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import styles from './AppVersionCard.module.css';
import Modal from '../common/Modal';
import StatusToggle from '../common/StatusToggle';
import {
    compareVersions,
    evaluateVersion,
    isValidVersion,
    PLATFORMS,
    readApiError,
    saveAppVersion,
    VERDICT,
} from '../../services/appVersionApi';

// Sunucu kaydından form durumu. Kayıt yoksa alanlar boş gelir — bu "yapılandırılmamış"
// demektir, hata değil.
const toForm = (config) => ({
    enabled: !!config?.enabled,
    minSupportedVersion: config?.minSupportedVersion || '',
    latestVersion: config?.latestVersion || '',
    storeUrl: config?.storeUrl || '',
});

const formatDateTime = (iso) => {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

const VERDICT_TEXT = {
    [VERDICT.DISABLED]: { label: 'KONTROL KAPALI', hint: 'Sürüm kontrolü kapalı; kullanıcıya hiçbir şey gösterilmez.', tone: 'off' },
    [VERDICT.MANDATORY]: { label: 'ZORUNLU', hint: 'Uygulama kullanılamaz; güncelleme ekranı kapatılamaz.', tone: 'danger' },
    [VERDICT.OPTIONAL]: { label: 'ÖNERİLEN', hint: 'Güncelleme önerilir; kullanıcı kapatıp devam edebilir.', tone: 'warn' },
    [VERDICT.NONE]: { label: 'GÜNCELLEME YOK', hint: 'Bu kullanıcı hiçbir uyarı görmez.', tone: 'ok' },
};

/**
 * Tek platformun (iOS / Android) sürüm kontrolü kartı.
 *
 * Validasyon backend'de de var; buradaki kopya yalnızca UX için — 400 almadan
 * önce hatayı yerinde göstermek için.
 */
const AppVersionCard = ({ config, onSaved }) => {
    const platform = config?.platform;
    const [form, setForm] = useState(() => toForm(config));
    const [showErrors, setShowErrors] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [confirmWarnings, setConfirmWarnings] = useState(null);
    const [previewVersion, setPreviewVersion] = useState('');

    const saved = useMemo(() => toForm(config), [config]);
    const isConfigured = !!(config?.minSupportedVersion || config?.latestVersion || config?.storeUrl);
    const updatedAt = formatDateTime(config?.updatedAt);

    // Platform metinleri tek yerde (servis) tanımlı; kart onları okur.
    const meta = PLATFORMS.find((p) => p.value === platform) || {};
    const label = meta.label || platform || '—';
    const storeLabel = meta.storeLabel || 'Mağaza URL\'i';
    const storePlaceholder = meta.storePlaceholder || 'https://';

    const errors = useMemo(() => {
        const next = {};
        const min = form.minSupportedVersion.trim();
        const latest = form.latestVersion.trim();
        const url = form.storeUrl.trim();

        // Kontrol kapalıysa boş bırakmak meşru: kayıt hiç yapılmamış olabilir.
        if (form.enabled) {
            if (!min) next.minSupportedVersion = 'Kontrol açıkken zorunlu minimum sürüm gerekli.';
            if (!latest) next.latestVersion = 'Kontrol açıkken güncel sürüm gerekli.';
            if (!url) next.storeUrl = 'Kontrol açıkken mağaza URL\'i gerekli.';
        }

        if (min && !isValidVersion(min)) next.minSupportedVersion = 'Biçim geçersiz. Yalnızca sayı ve nokta: 1.2.0';
        if (latest && !isValidVersion(latest)) next.latestVersion = 'Biçim geçersiz. Yalnızca sayı ve nokta: 1.5.0';

        // Sürüm karşılaştırması sayısal segment bazlı — "1.10" > "1.9".
        if (isValidVersion(min) && isValidVersion(latest) && compareVersions(min, latest) > 0) {
            next.minSupportedVersion = 'Zorunlu minimum sürüm, güncel sürümden büyük olamaz.';
        }

        if (url && !/^https:\/\//i.test(url)) {
            next.storeUrl = 'Mağaza URL\'i https:// ile başlamalı.';
        }

        return next;
    }, [form]);

    const hasErrors = Object.keys(errors).length > 0;

    const isDirty =
        form.enabled !== saved.enabled ||
        form.minSupportedVersion.trim() !== saved.minSupportedVersion ||
        form.latestVersion.trim() !== saved.latestVersion ||
        form.storeUrl.trim() !== saved.storeUrl;

    const setField = (key) => (event) => {
        const value = event.target.value;
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const previewVerdict = evaluateVersion(previewVersion.trim(), {
        enabled: form.enabled,
        minSupportedVersion: form.minSupportedVersion.trim(),
        latestVersion: form.latestVersion.trim(),
    });

    /**
     * Kaydetmenin kullanıcıları anında etkileyeceği durumları toplar.
     * Boş dönerse onay modalı gösterilmez.
     */
    const collectWarnings = () => {
        const warnings = [];
        const min = form.minSupportedVersion.trim();
        const minChanged = min !== saved.minSupportedVersion;

        if (minChanged && min) {
            const raised = !saved.minSupportedVersion || compareVersions(min, saved.minSupportedVersion) > 0;
            if (raised) {
                warnings.push(
                    `${min} sürümünün altındaki TÜM ${label} kullanıcıları güncellemeye zorlanacak — uygulamayı kapatamayacakları bir ekran görecekler.`
                );
            } else {
                warnings.push(
                    `Zorunlu eşik ${saved.minSupportedVersion} → ${min} olarak düşürülüyor; daha az kullanıcı zorlanacak.`
                );
            }
        }

        if (form.enabled && !saved.enabled && min) {
            warnings.push(`Sürüm kontrolü ${label} için açılıyor; eşik hemen devreye girer.`);
        }

        return warnings;
    };

    const doSave = async () => {
        setIsSaving(true);
        try {
            const payload = {
                minSupportedVersion: form.minSupportedVersion.trim(),
                latestVersion: form.latestVersion.trim(),
                storeUrl: form.storeUrl.trim(),
                enabled: form.enabled,
            };
            const updated = await saveAppVersion(platform, payload);
            toast.success(`${label} sürüm ayarları kaydedildi.`);
            setShowErrors(false);
            setConfirmWarnings(null);
            onSaved?.(platform, updated);
        } catch (error) {
            const { message } = readApiError(error, 'Sürüm ayarları kaydedilemedi.');
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveClick = () => {
        if (hasErrors) {
            setShowErrors(true);
            toast.error('Formda hatalı alanlar var.');
            return;
        }
        const warnings = collectWarnings();
        if (warnings.length > 0) {
            setConfirmWarnings(warnings);
            return;
        }
        doSave();
    };

    const handleReset = () => {
        setForm(toForm(config));
        setShowErrors(false);
    };

    const fieldClass = (key) =>
        `${styles.input} ${showErrors && errors[key] ? styles.inputError : ''}`;

    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <div className={styles.cardTitleRow}>
                    <h2 className={styles.cardTitle}>{label}</h2>
                    {isConfigured ? (
                        <span className={`${styles.badge} ${form.enabled ? styles.badgeOn : styles.badgeOff}`}>
                            {form.enabled ? 'Kontrol aktif' : 'Kontrol kapalı'}
                        </span>
                    ) : (
                        <span className={`${styles.badge} ${styles.badgeUnset}`}>Yapılandırılmamış</span>
                    )}
                </div>
                <StatusToggle
                    label={`${label} sürüm kontrolü`}
                    checked={form.enabled}
                    disabled={isSaving}
                    onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                    onText="Sürüm kontrolü aktif"
                    offText="Sürüm kontrolü kapalı"
                />
            </div>

            {!form.enabled && (
                <p className={styles.disabledNote}>
                    Kontrol kapalıyken {label} kullanıcılarına hiçbir güncelleme uyarısı gösterilmez.
                </p>
            )}

            <div className={styles.fields}>
                <div className={styles.field}>
                    <label className={styles.label} htmlFor={`min-${platform}`}>
                        Zorunlu minimum sürüm
                    </label>
                    <p className={styles.labelHint}>
                        Bu sürümün <strong>altındaki</strong> kullanıcılar uygulamayı kullanamaz. Eşite eşit sürüm zorlanmaz.
                    </p>
                    <input
                        id={`min-${platform}`}
                        type="text"
                        inputMode="decimal"
                        className={fieldClass('minSupportedVersion')}
                        placeholder="1.0.0"
                        value={form.minSupportedVersion}
                        onChange={setField('minSupportedVersion')}
                        disabled={isSaving}
                    />
                    {showErrors && errors.minSupportedVersion && (
                        <span className={styles.errorText}>{errors.minSupportedVersion}</span>
                    )}
                </div>

                <div className={styles.field}>
                    <label className={styles.label} htmlFor={`latest-${platform}`}>
                        Güncel sürüm
                    </label>
                    <p className={styles.labelHint}>
                        Bunun altındaki (ama eşiğin üstündeki) kullanıcılara güncelleme <strong>önerilir</strong>; kapatabilirler.
                    </p>
                    <input
                        id={`latest-${platform}`}
                        type="text"
                        inputMode="decimal"
                        className={fieldClass('latestVersion')}
                        placeholder="1.0.0"
                        value={form.latestVersion}
                        onChange={setField('latestVersion')}
                        disabled={isSaving}
                    />
                    {showErrors && errors.latestVersion && (
                        <span className={styles.errorText}>{errors.latestVersion}</span>
                    )}
                </div>

                <div className={styles.field}>
                    <label className={styles.label} htmlFor={`store-${platform}`}>
                        {storeLabel}
                    </label>
                    <p className={styles.labelHint}>
                        Güncelleme ekranındaki butonun açacağı adres.
                    </p>
                    <input
                        id={`store-${platform}`}
                        type="url"
                        className={fieldClass('storeUrl')}
                        placeholder={storePlaceholder}
                        value={form.storeUrl}
                        onChange={setField('storeUrl')}
                        disabled={isSaving}
                    />
                    {showErrors && errors.storeUrl && (
                        <span className={styles.errorText}>{errors.storeUrl}</span>
                    )}
                </div>
            </div>

            {/* Önizleme, KAYDEDİLMEMİŞ form değerlerine göre hesaplanır —
                kaydetmeden önce sonucu görmek için. */}
            <div className={styles.preview}>
                <div className={styles.previewHeader}>
                    <span className={styles.previewTitle}>Önizleme</span>
                    <span className={styles.previewNote}>Formdaki değerlere göre (kaydedilmemiş)</span>
                </div>
                <div className={styles.previewRow}>
                    <input
                        type="text"
                        inputMode="decimal"
                        className={styles.previewInput}
                        placeholder="Kullanıcının sürümü — örn. 1.3.0"
                        value={previewVersion}
                        onChange={(e) => setPreviewVersion(e.target.value)}
                    />
                    {previewVersion.trim() === '' ? (
                        <span className={styles.previewIdle}>Bir sürüm girin</span>
                    ) : previewVerdict === null ? (
                        <span className={styles.previewIdle}>Geçersiz sürüm biçimi</span>
                    ) : (
                        <span className={`${styles.verdict} ${styles[`verdict_${VERDICT_TEXT[previewVerdict].tone}`]}`}>
                            {VERDICT_TEXT[previewVerdict].label}
                        </span>
                    )}
                </div>
                {previewVersion.trim() !== '' && previewVerdict !== null && (
                    <p className={styles.previewHint}>{VERDICT_TEXT[previewVerdict].hint}</p>
                )}
            </div>

            <div className={styles.cardFooter}>
                <div className={styles.meta}>
                    {updatedAt ? (
                        <>
                            <span className={styles.metaLine}>Son güncelleme: {updatedAt}</span>
                            {config?.updatedBy && (
                                <span className={styles.metaLine}>Güncelleyen: {config.updatedBy}</span>
                            )}
                        </>
                    ) : (
                        <span className={styles.metaLine}>Bu platform için henüz kayıt yapılmamış.</span>
                    )}
                </div>
                <div className={styles.actions}>
                    {isDirty && (
                        <button
                            type="button"
                            className={styles.ghostButton}
                            onClick={handleReset}
                            disabled={isSaving}
                        >
                            Geri al
                        </button>
                    )}
                    <button
                        type="button"
                        className={styles.saveButton}
                        onClick={handleSaveClick}
                        disabled={isSaving || !isDirty}
                    >
                        {isSaving ? 'Kaydediliyor…' : 'Kaydet'}
                    </button>
                </div>
            </div>

            <Modal
                isOpen={!!confirmWarnings}
                onClose={() => setConfirmWarnings(null)}
                title={`${label} — Değişikliği onayla`}
            >
                <div className={styles.confirmBody}>
                    <ul className={styles.confirmList}>
                        {(confirmWarnings || []).map((warning, index) => (
                            <li key={index}>{warning}</li>
                        ))}
                    </ul>
                    <p className={styles.confirmQuestion}>Devam etmek istiyor musunuz?</p>
                    <div className={styles.confirmActions}>
                        <button
                            type="button"
                            className={styles.ghostButton}
                            onClick={() => setConfirmWarnings(null)}
                            disabled={isSaving}
                        >
                            Vazgeç
                        </button>
                        <button
                            type="button"
                            className={styles.dangerButton}
                            onClick={doSave}
                            disabled={isSaving}
                        >
                            {isSaving ? 'Kaydediliyor…' : 'Evet, kaydet'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AppVersionCard;
